import { CONTRACT_STATUSES, type UsageReportV2ReportItem } from '@vy/types';
import { useMemo } from 'react';
import type { FilterField } from '../../../../components/new-design/vyTableFilter';
import {
  buildCustomerFilterField,
  buildPlanFilterField,
  collectIdNameOptions,
  collectUniqueOptions,
  filterNonEmptySelectFields,
  MULTISELECT_OPERATORS,
} from '../../reports-components/shared-filter-fields';
import { splitPascalCase } from '../usage-report-v2-report-components/usage-report-v2-format-helpers';
import { USAGE_REPORT_V2_FILTER_FIELD_IDS } from '../utils/usage-report-v2-filter-field-ids';

export function useUsageReportV2FilterFields(
  reportItems: UsageReportV2ReportItem[],
): FilterField[] {
  return useMemo(() => {
    const customers = collectIdNameOptions(
      reportItems.map((item) => ({ id: item.customer.id, name: item.customer.name })),
    );
    const plans = collectUniqueOptions(reportItems.map((item) => item.contract.name));
    
    const uniquePricingModels = new Set(reportItems.map((item) => item.product.pricingModel));
    const sortedPricingModels = Array.from(uniquePricingModels).sort((modelA, modelB) =>
      modelA.localeCompare(modelB),
    );
    const pricingModels = sortedPricingModels.map((model) => ({
      value: model,
      label: splitPascalCase(model),
    }));

    const allFields: FilterField[] = [
      buildCustomerFilterField(customers),
      buildPlanFilterField(plans),
      {
        id: USAGE_REPORT_V2_FILTER_FIELD_IDS.ContractStatus,
        label: 'Contract Status',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: Object.values(CONTRACT_STATUSES).map((status) => ({ value: status, label: status })),
      },
      {
        id: USAGE_REPORT_V2_FILTER_FIELD_IDS.PricingType,
        label: 'Pricing Type',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: pricingModels,
      },
    ];

    return filterNonEmptySelectFields(allFields);
  }, [reportItems]);
}
